import { Divider, Stack } from '@chakra-ui/react'
import { ReactNode, useEffect, useState } from 'react'

import { ISelectOption } from '../../../components/Select'
import { EmployeeService } from '../../../services/Employee/EmployeeService'
import { TEmployeeStatus } from '../../../services/Employee/types'
import { JobPosition } from '../../../services/JobPosition/types'
import { TFormErrors } from '../Admission/admissionForm'
import BenefitsStep from '../Admission/steps/BenefitsStep'
import ContactsStep from '../Admission/steps/ContactsStep'
import PersonalStep from '../Admission/steps/PersonalStep'
import TransportStep from '../Admission/steps/TransportStep'
import { IEmployeeForm } from './employeeForm'
import EmployeeProfessionalFields from './EmployeeProfessionalFields'

/** A full name has a surname; before that there is nothing to suggest. */
const hasSurname = (name: string) => name.trim().split(/\s+/).length > 1

/**
 * Usernames offered for the name being typed, read again a moment after it stops changing.
 * `exceptId` keeps the edited collaborator's own username available to them.
 */
export function useUsernameSuggestions(name: string, enabled: boolean, exceptId?: number): string[] | null {
  const [usernames, setUsernames] = useState<string[] | null>([])

  useEffect(() => {
    if (!enabled) return
    if (!hasSurname(name)) {
      setUsernames([])
      return
    }

    setUsernames(null)
    const timer = setTimeout(() => {
      new EmployeeService()
        .usernames(name, exceptId)
        .then(setUsernames)
        .catch(() => setUsernames([]))
    }, 400)

    return () => clearTimeout(timer)
  }, [name, enabled, exceptId])

  return usernames
}

interface IEmployeeFormPanelsProps {
  form: IEmployeeForm
  update: (patch: Partial<IEmployeeForm>) => void
  errors: TFormErrors
  isDisabled: boolean
  status: TEmployeeStatus
  photo: File | null
  photoUrl: string | null
  onPhotoChange: (photo: File) => void
  roles: JobPosition[]
  sectors: ISelectOption<number>[]
  usernames: string[] | null
  currentRoleId?: number | null
}

/**
 * The editable panels of a collaborator, in the order of the sheet's tabs: the admission form's
 * own steps in panel mode (only name and e-mail required) plus the professional fields.
 */
export function employeeFormPanels(props: IEmployeeFormPanelsProps): {
  personal: ReactNode
  professional: ReactNode
  contacts: ReactNode
  benefits: ReactNode
} {
  const stepProps = { form: props.form, update: props.update, errors: props.errors, isDisabled: props.isDisabled }
  const usesVoucher = props.form.transport_benefits.includes('transport_voucher')

  return {
    personal: (
      <PersonalStep
        {...stepProps}
        mode="panel"
        photo={props.photo}
        photoUrl={props.photoUrl}
        onPhotoChange={props.onPhotoChange}
      />
    ),
    professional: (
      <EmployeeProfessionalFields
        {...stepProps}
        status={props.status}
        roles={props.roles}
        sectors={props.sectors}
        usernames={props.usernames}
        currentRoleId={props.currentRoleId}
      />
    ),
    contacts: <ContactsStep {...stepProps} mode="panel" />,
    benefits: (
      <Stack spacing={8}>
        <BenefitsStep {...stepProps} mode="panel" />
        {usesVoucher && (
          <>
            <Divider />
            <TransportStep {...stepProps} mode="panel" />
          </>
        )}
      </Stack>
    )
  }
}
